import { useState, useEffect } from "react";
import { ImageValidation } from "../Validation/ImageValidation";
import { toast } from "react-toastify";
import config from "../../utils/config";
import Rejectreason from "./Rejectreason";
import { useKycAadhaarVerifyMutation } from "../../redux/kycApi";

const { imageUrl } = config;

const Aadhaar = ({ kycTrack, refetch }) => {
  const [aadhaarNumber, setAadhaarNumber] = useState("");
  const [aadhaarFront, setAadhaarFront] = useState("");
  const [aadhaarBack, setAadhaarBack] = useState("");
  const [previewFront, setPreviewFront] = useState("/assets/img/icon2.png");
  const [previewBack, setPreviewBack] = useState("/assets/img/icon2.png");

  const [aadhaarNumberError, setAadhaarNumberError] = useState("");
  const [aadhaarFrontError, setAadhaarFrontError] = useState("");
  const [aadhaarBackError, setAadhaarBackError] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [aadhaarStatus, setAadhaarStatus] = useState("0");
  const [btnDisable, setBtnDisable] = useState(false);

  const [kycAadhaarVerify] = useKycAadhaarVerifyMutation();

  useEffect(() => {
    const aadhaarData = kycTrack?.progress?.aadhaar_verification?.data;

    const loadImageAsFile = async (url, filename = "aadhaar.jpg") => {
      try {
        const res = await fetch(url);
        const blob = await res.blob();
        return new File([blob], filename, { type: blob.type });
      } catch (err) {
        console.error("Failed to convert Aadhaar image URL to File:", err);
        return null;
      }
    };

    if (aadhaarData) {
      const frontUrl = imageUrl + aadhaarData.front_image;
      const backUrl = imageUrl + aadhaarData.back_image;

      setAadhaarNumber(aadhaarData.aadhaar_number || "");
      setRejectionReason(aadhaarData.reason || "");
      setPreviewFront(frontUrl);
      setPreviewBack(backUrl);
      setAadhaarStatus(
        kycTrack?.progress?.aadhaar_verification?.status || "pending"
      );

      // 👇 Convert URLs into File objects so submit works directly
      loadImageAsFile(frontUrl, "aadhaar_front.jpg").then((file) => {
        if (file) setAadhaarFront(file);
      });

      loadImageAsFile(backUrl, "aadhaar_back.jpg").then((file) => {
        if (file) setAadhaarBack(file);
      });
    }
  }, [kycTrack]);

  const status = kycTrack?.progress?.aadhaar_verification?.status;
  const isEditable = status === "pending" || status === "rejected";

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "aadhaarNumber") {
      setAadhaarNumber(value);

      if (!value) {
        setAadhaarNumberError("This field is required");
      } else if (!/^\d{12}$/.test(value)) {
        setAadhaarNumberError(
          "Aadhaar number must be exactly 12 digits number only"
        );
      } else if (/^[01]/.test(value)) {
        setAadhaarNumberError("Aadhaar number cannot start with 0 or 1");
      } else if (/^(\d)\1{11}$/.test(value)) {
        setAadhaarNumberError("Aadhaar number cannot have all digits same");
      } else {
        setAadhaarNumberError("");
      }
    }
  };

  const handleImageChange = (e) => {
    const { name, files } = e.target;
    const file = files[0];
    if (!file) return;

    // Validate format/size first
    const error = ImageValidation(
      name === "front" ? "Aadhaar front" : "Aadhaar back",
      file
    );
    if (error) {
      if (name === "front") setAadhaarFrontError(error);
      else setAadhaarBackError(error);
      return;
    }

    // Check duplicates
    if (name === "front") {
      if (aadhaarBack) {
        if (
          aadhaarBack.name === file.name || // same filename
          aadhaarBack.size === file.size // same content size (quick check)
        ) {
          setAadhaarFrontError("Front image cannot be same as back image");
          return;
        }
      }
      setAadhaarFrontError("");
      setAadhaarFront(file);
      setPreviewFront(URL.createObjectURL(file));
    } else {
      if (aadhaarFront) {
        if (
          aadhaarFront.name === file.name || // same filename
          aadhaarFront.size === file.size // same content size (quick check)
        ) {
          setAadhaarBackError("Back image cannot be same as front image");
          return;
        }
      }
      setAadhaarBackError("");
      setAadhaarBack(file);
      setPreviewBack(URL.createObjectURL(file));
    }
  };

  const handleValidation = (event) => {
    if (
      [
        "e",
        "E",
        "-",
        "+",
        "!",
        "@",
        "#",
        "$",
        "%",
        "^",
        "&",
        "*",
        "(",
        ")",
      ].includes(event.key)
    ) {
      event.preventDefault();
    }
  };

  const handleSubmit = async () => {
    // ✅ Block submission if Aadhaar invalid

    if (!aadhaarNumber || !aadhaarFront || !aadhaarBack) {
      setAadhaarNumberError(!aadhaarNumber ? "This field is required" : "");
      setAadhaarFrontError(!aadhaarFront ? "This field is required" : "");
      setAadhaarBackError(!aadhaarBack ? "This field is required" : "");
      return;
    }

    // ✅ Block submission if any validation errors exist
    if (aadhaarNumberError || aadhaarFrontError || aadhaarBackError) {
      return;
    }

    setBtnDisable(true);
    setTimeout(() => setBtnDisable(false), 3000);

    try {
      const formData = new FormData();
      formData.append("aadhar_number", aadhaarNumber);
      formData.append("front", aadhaarFront);
      formData.append("back", aadhaarBack);

      const { data, error } = await kycAadhaarVerify(formData);

      if (error) {
        toast.error(error?.data?.message || "Something went wrong");
      } else if (data?.status) {
        toast.success(data?.message || "Aadhaar submitted successfully");
        refetch();
      } else {
        toast.error(data?.message || "Submission failed");
      }
    } catch (err) {
      toast.error("Unexpected error occurred");
    } finally {
      setBtnDisable(false);
    }
  };

  return (
    <div className="aadhaar-outer" id="proof">
      <div className="">
        {/* <div className="col-md-3 col-12"></div> */}
        <div className="">
          <div className="white_box">
            <h6 className="position-relative">Aadhaar Card Verification</h6>
            <b className="mt-0">Requirements</b>
            <ul className="dit_ul mb-3">
              <li>The photo/scan must be clear, in color and uncompressed.</li>
              <li>The photo/scan must be not edited and not cropped.</li>
              {/* <li>
                The document must be valid at least 3 months after submission.
              </li> */}
              <li>
                Your full name and date of birth should exactly match your
                document.
              </li>
              <li>Min size 10 Kb / Max size 2 Mb.</li>
            </ul>

            <div className="form-group col-md-6">
              <label>Aadhaar Number</label>
              <input
                type="text"
                className="form-control"
                name="aadhaarNumber"
                placeholder="Enter Aadhaar Number"
                value={aadhaarNumber}
                onKeyDown={(e) => {
                  // Block arrow up/down and 'e', '+', '-', '.', etc.
                  if (
                    e.key === "ArrowUp" ||
                    e.key === "ArrowDown" ||
                    e.key === "e" ||
                    e.key === "+" ||
                    e.key === "-" ||
                    e.key === "."
                  ) {
                    e.preventDefault();
                  }
                }}
                onWheel={(e) => e.target.blur()} // Prevent scroll from changing value
                onChange={handleInputChange}
                maxLength={12} // restrict to 12 chars directly
                disabled={!isEditable}
              />

              {aadhaarNumberError && (
                <div className="text-danger">{aadhaarNumberError}</div>
              )}
            </div>

            <div className="fs12">
              <div className="row">
                <div className="form-group col-md-4">
                  <div id="file-upload-form" className="uploader">
                    <input
                      type="file"
                      id="file-upload"
                      name="front"
                      accept="image/*"
                      onChange={handleImageChange}
                      // onBlur={this.onBlurIdentityImage}
                      // disabled={
                      //   userInfoLength > 0 &&
                      //   userInfo[0].identity_status !== "0" &&
                      //   userInfo[0].identity_status !== "3"
                      //     ? true
                      //     : false
                      // }
                      disabled={!isEditable}
                    />
                    <label for="file-upload" id="file-drag">
                      <img
                        src={previewFront}
                        className="img-fluid22"
                        alt="Identity"
                      />
                      <div id="start">
                        <span className="fasi"></span>
                        <div>Front image</div>
                        <div id="notimage" className="hidden">
                          Please select an image
                        </div>
                      </div>
                      <div id="response" className="hidden">
                        <div id="messages"></div>
                        <progress
                          className="progress"
                          id="file-progress"
                          value="0"
                        >
                          <span>0</span>%
                        </progress>
                      </div>
                    </label>
                  </div>
                  <p className="mt-0">
                    *Only jpeg, jpg &amp; png files supported <br />
                    *For changing image, click on image upload section again.
                    <br />
                    *Front image name can't be same as back image.
                  </p>
                  {aadhaarFrontError && (
                    <div className="text-danger">{aadhaarFrontError}</div>
                  )}
                </div>

                <div className="form-group col-md-4">
                  <div id="file-upload-form" className="uploader">
                    <input
                      type="file"
                      id="file-upload-2"
                      name="back"
                      accept="image/*"
                      onChange={handleImageChange}
                      // disabled={
                      //   userInfoLength > 0 &&
                      //   userInfo[0].identity_status !== "0" &&
                      //   userInfo[0].identity_status !== "3"
                      //     ? true
                      //     : false
                      // }
                      disabled={!isEditable}
                    />
                    <label for="file-upload-2" id="file-drag">
                      <img
                        src={previewBack}
                        className="img-fluid22"
                        alt="Identity"
                      />
                      <div id="start">
                        <span className="fasi"></span>
                        <div>Back image</div>
                        <div id="notimage" className="hidden">
                          Please select an image
                        </div>
                      </div>
                      <div id="response" className="hidden">
                        <div id="messages"></div>
                        <progress
                          className="progress"
                          id="file-progress"
                          value="0"
                        >
                          <span>0</span>%
                        </progress>
                      </div>
                    </label>
                  </div>
                  <p className="mt-0">
                    *Only jpeg, jpg &amp; png files supported <br />
                    *For changing image, click on image upload section again.
                    <br />
                    *Back image name can't be same as front image.
                  </p>
                  {aadhaarBackError && (
                    <div className="text-danger">{aadhaarBackError}</div>
                  )}
                </div>
              </div>
            </div>

            <Rejectreason reason={rejectionReason} status={aadhaarStatus} />

            {isEditable && (
              <button
                className="btn btn_man fs12 w100px"
                onClick={handleSubmit}
                disabled={btnDisable}
              >
                {status === "pending" ? "Submit" : "Re-submit"}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Aadhaar;
